// Core design tokens for Lutely Web
// These tokens provide consistent styling across all components

export const modalTokens = {
  // Modal container
  wrapper: "fixed inset-0 flex items-center justify-center p-4",
  overlay: "fixed inset-0 bg-black/50 backdrop-blur-sm",
  container:
    "relative bg-gray-100 rounded-lg shadow-[0_14px_20px_-1px_rgba(5,12,43,0.14)]",

  // Modal content spacing
  content: "px-6 pt-8 pb-6 flex-1 ", // 24px sides, 32px top, 24px bottom

  // Close button
  closeButton:
    "absolute right-3 top-3 w-10 h-10 rounded-full flex items-center justify-center text-[#525966] hover:text-brand-primary",

  // Footer
  footer:
    "px-6 py-4 border-t border-gray-200 bg-white rounded-b-lg flex justify-end gap-3 position-sticky bottom-0",
};

// Modal size tokens — choose exactly one per modal instance
export const modalSizes = {
  sm: "w-full max-w-[400px] mx-4",
  smd: "w-full max-w-[600px] mx-4",
  md: "w-full max-w-[800px] mx-4",
  lg: "w-full max-w-[900px] mx-4",
};

export const typographyTokens = {
  // Modal title - Figtree SemiBold 18px
  modalTitle:
    "font-figtree font-bold text-[24px] leading-[21.6px] tracking-[-0.36px] text-blue-900",

  // Modal title (Sans) - Open Sans 18 SemiBold (LUT-114 modal)
  modalTitleSans:
    "font-open-sans font-semibold text-[18px] leading-[25.2px] text-black",

  // Form labels - Open Sans
  label:
    "font-open-sans font-semibold text-[14px] leading-[19.6px] text-black block mb-2",

  // Helper text
  helperText: "font-open-sans text-[12px] leading-[16.8px] text-gray-600 mt-2",
};

export const formTokens = {
  // Section spacing - 40px between sections
  sectionSpacing: "space-y-10",

  // Text inputs - 40px height, white bg, #E9EDF3 border, 4px radius
  input:
    "h-[40px] w-full bg-white border border-gray-300 rounded px-3 font-open-sans text-[14px] text-black placeholder:text-gray-400 focus:outline-none focus:border-gray-400 transition-colors",

  // Textarea - 80px height
  textarea:
    "min-h-[80px] w-full bg-white border border-gray-300 rounded px-3 py-2 font-open-sans text-[14px] text-black placeholder:text-gray-400 focus:outline-none focus:border-gray-400 resize-none transition-colors",

  // Specific field widths
  urlInput:
    "h-[40px] w-[420px] bg-white border border-gray-300 rounded px-3 font-open-sans text-[14px] text-black placeholder:text-gray-400 focus:outline-none focus:border-gray-400 transition-colors",
  titleInput:
    "h-[40px] w-[284px] bg-white border border-gray-300 rounded px-3 font-open-sans text-[14px] text-black placeholder:text-gray-400 focus:outline-none focus:border-gray-400 transition-colors",
};

export const buttonTokens = {
  // Primary button - 125×40px pill, black background
  primary:
    "cursor-pointer h-[40px] px-5 min-w-[125px] rounded-full bg-black hover:bg-gray-900 text-white font-open-sans font-semibold text-[14px] transition-colors disabled:opacity-50 disabled:cursor-not-allowed",

  // Cancel button - text style, no background
  cancel:
    "cursor-pointer h-[40px] px-4 bg-transparent font-open-sans font-semibold text-[14px] text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors",

  // Link button - blue text
  addLink:
    "cursor-pointer font-open-sans text-[14px] text-[#176AE6] hover:text-[#1458C0] hover:underline transition-colors disabled:opacity-50 disabled:cursor-not-allowed",

  // Icon button - trash
  trash:
    "cursor-pointer w-9 h-9 border border-gray-300 rounded-md flex items-center justify-center text-gray-500 hover:text-gray-700 hover:border-gray-300 transition-colors disabled:opacity-30",

  // Update image button
  updateImage:
    "cursor-pointer h-[34px] px-4 bg-black text-white font-open-sans text-[14px] rounded-full",

  uploadButton:
    "enabled:bg-brand-primary enabled:text-white disabled:pointer-events-none rounded-full cursor-pointer w-auto pl-[15px] pr-[15px]",

  // Hero CTA buttons - for use on dark/image backgrounds (banners, featured sections)
  // Based on Figma specs: h-10 (40px), px-5 (20px), rounded-full, Open Sans semibold 16px
  heroPrimary:
    "inline-flex items-center justify-center gap-1.5 h-10 px-5 rounded-full bg-white text-background-neutral-inverted font-open-sans font-semibold text-md whitespace-nowrap hover:bg-white/90 transition-colors",
  heroSecondary:
    "inline-flex items-center justify-center gap-1.5 h-10 px-5 rounded-full bg-white/15 text-white font-open-sans font-semibold text-md whitespace-nowrap hover:bg-white/25 transition-colors",
};

// Auth screens (Login/Forgot/Sign Up)
export const authTokens = {
  // Page-level wrapper: centers card while leaving room for header/footer
  page: "min-h-[calc(100vh-180px)] flex items-center justify-center",
  // Card container: responsive white panel, radius 8, same shadow as modals
  card: "w-full max-w-[600px] bg-white rounded-[8px] shadow-[0_14px_20px_-1px_rgba(5,12,43,0.14)] px-4 py-6 sm:px-6 sm:pt-8 sm:pb-6",
  // Title: Figtree 24 SemiBold centered
  title:
    "font-figtree font-semibold text-[24px] leading-[28.8px] tracking-[-0.24px] text-black text-center",
  // Primary CTA: pill 40px tall, full width
  primaryCta:
    "h-[40px] w-full rounded-full bg-blue-600 hover:bg-blue-700 text-white font-open-sans font-semibold text-[16px] transition-colors cursor-pointer",
};

export const avatarTokens = {
  container:
    "w-24 h-24 rounded-full bg-gray-200 overflow-hidden flex items-center justify-center",
  image: "w-full h-full object-cover",
};

// Right Panel (LUT-104) tokens
export const panelTokens = {
  // Outer container within grid (sticky panel)
  container: "w-[320px] sticky top-4 self-start rounded-[8px] bg-white",
  // Inner padding and vertical rhythm
  inner: "pt-2 pb-3 px-3 space-y-4",
  // Section wrapper with uniform spacing
  section: "space-y-2",
  // Section heading text (Open Sans 14 SemiBold)
  heading:
    "font-open-sans font-semibold text-[14px] leading-[19.6px] text-slate-900",
};

export const statTokens = {
  grid: "grid grid-cols-2 gap-x-6 gap-y-4",
  item: "flex items-center gap-3",
  // 32px circle holding a 20px icon
  iconCircle:
    "w-8 h-8 rounded-full bg-black text-white flex items-center justify-center shrink-0",
  value:
    "font-open-sans font-semibold text-[14px] leading-[19.6px] text-slate-900",
  label: "font-open-sans text-[12px] leading-[16.8px] text-slate-600",
};

export const linkRowTokens = {
  row: "h-[34px] rounded flex items-center justify-between hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black/20 transition-colors",
  text: "font-open-sans font-semibold text-[14px] leading-[19.6px] text-slate-900",
  right: "flex items-center gap-1 text-slate-700",
};

export const chipTokens = {
  group: "flex flex-wrap gap-2",
  chip: "inline-flex items-center gap-1 px-2 h-7 rounded-md border border-slate-200 bg-slate-50 text-slate-700 text-[12px] font-open-sans",
};

export const playerTokens = {
  shell: "fixed left-0 right-0 bottom-0 z-[60] w-screen",
  pad: "w-full pb-[calc(env(safe-area-inset-bottom))]",
  container:
    "pointer-events-auto flex h-[72px] w-full items-center gap-6 border-t border-white/12 bg-[#050506] px-6 text-white shadow-[0_-4px_12px_rgba(0,0,0,0.25)] backdrop-blur-sm",
  transportCluster: "flex items-center gap-3",
  transportButton:
    "flex h-10 w-10 items-center justify-center rounded-full text-white transition hover:bg-white/10 disabled:opacity-35 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#43DDA7] focus-visible:ring-offset-2 focus-visible:ring-offset-black",
  playButton:
    "relative flex h-12 w-12 items-center justify-center rounded-full bg-[#E2E6ED] text-black shadow-[0_6px_16px_rgba(0,0,0,0.35)] transition hover:scale-[1.01] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#43DDA7] focus-visible:ring-offset-2 focus-visible:ring-offset-black",
  playButtonHighlight: "absolute inset-0 rounded-full border border-white/20",
  metadataBlock: "flex min-w-[220px] items-center gap-3",
  albumArt:
    "h-10 w-10 overflow-hidden rounded-[10px] bg-[#1B1D25] ring-1 ring-white/10",
  title:
    "font-['Figtree'] text-[16px] font-semibold leading-[20px] tracking-[-0.18px] text-white",
  creator:
    "font-open-sans text-[12px] font-medium leading-[14px] text-white/70",
  timelineSection: "flex min-w-[320px] flex-1 items-center gap-3",
  timeLabel:
    "font-open-sans text-[13px] font-semibold text-white/80 tabular-nums",
  actionCluster: "flex items-center gap-2.5",
  actionButton:
    "flex h-10 w-10 items-center justify-center rounded-full text-white transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#43DDA7] focus-visible:ring-offset-2 focus-visible:ring-offset-black",
};

// White content cards used across the audiolab edit pages (albums, tracks).
export const cardTokens = {
  card: "flex flex-col gap-[16px] rounded-[8px] border border-border-tertiary bg-white p-[20px]",
  title: "text-[13px] font-bold uppercase tracking-[0.08em] text-brand-primary",
  fieldLabel: "text-[14px] font-semibold text-[#050C2B]",
  helperText: "font-open-sans text-[12px] font-normal text-[#525966]",
};

// One shared look for every dropdown, selector and action menu.
export const menuTokens = {
  panel:
    "z-50 max-h-[min(var(--menu-max-height),var(--radix-dropdown-menu-content-available-height,var(--menu-max-height)))] overflow-y-auto rounded-[10px] border border-border-quarternary bg-white p-[6px] shadow-[0px_10px_18px_-1px_rgba(5,12,43,0.14)]",
  // No text color in the base: cn() is plain clsx (no tailwind-merge), so a
  // color here would fight per-item colors on stylesheet order. Pair with
  // itemNeutral or itemDestructive.
  item: "w-full h-[34px] px-[8px] rounded-[6px] flex items-center gap-[8px] text-left text-[14px] font-semibold cursor-pointer transition-colors outline-none focus-visible:outline-none hover:bg-background-tertiary data-[highlighted]:bg-background-tertiary data-[state=open]:bg-background-tertiary data-[disabled]:text-text-tertiary data-[disabled]:cursor-default",
  itemNeutral: "text-text-primary",
  itemSelected: "bg-background-accent-quinary text-brand-primary",
  itemDestructive:
    "text-error-bold hover:bg-error-bold/10 data-[highlighted]:bg-error-bold/10",
  separator: "my-[6px] h-px bg-border-quarternary",
};

export const marketplaceTokens = {
  filterItem:
    "h-[34px] px-[8px] cursor-pointer transition-all text-[14px] font-semibold justify-start capitalize ease-in-out rounded-[4px]",
  filterItemInactive:
    "bg-page hover:bg-[#ECEFF4] hover:text-background-neutral-inverted text-text-secondary",
  filterItemActive: "bg-[#ECEFF4] text-background-neutral-inverted",
  sectionHeader: "text-blue-900 font-semibold text-[24px] mb-[5px]",
};
